import { DEFAULT_GUARD_RAILS } from "./constants";
import {
  buildCubeFiltersFromFilterModule,
  filterModuleHasContent,
  hydrateFilterModuleFromCubeFilters,
} from "./cubeFilters";
import { timePeriodToCustomDate } from "../../../helper/Quadrant/quadrantHelper";
import {
  formatIsoDateForInput,
  frequencyValuesFromDayIndices,
  generateId,
  getCampaignBaseBudget,
  getMinCampaignBudget,
  parseNumOrEmpty,
  selectedDayIndicesFromFrequency,
} from "./helpers";

export function createDefaultCampaignBudget(campaign) {
  const base = getCampaignBaseBudget(campaign);
  return {
    status: "enabled",
    base_value: base,
    min_value: getMinCampaignBudget(),
    max_value: "",
  };
}

export function createDefaultSlotCampaignBudget(multiCampaign) {
  return {
    change_type: multiCampaign ? "PERCENT_INCREASE" : "PERCENT_INCREASE",
    hasCustomType: false,
    change_value: "",
    hasCustomValue: false,
  };
}

export function createDefaultSlot(multiCampaign = false) {
  return {
    localId: generateId(),
    durationId: `dur-${generateId()}`,
    status: "active",
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
    enableCompounding: false,
    perfFilter: {
      enabled: false,
      timePeriod: "LAST_7_DAY",
      customDate: "last_7d",
      cubeFilters: "",
      filterModuleFilters: null,
    },
    campaignBudgets: {},
  };
}

export function createInitialFormState() {
  return {
    ruleName: "",
    status: "Active",
    activeCampaignFilter: "All",
    selectedCampaignIds: [],
    campaignMeta: {},
    campaignTableBudgets: {},
    automationMode: "ALL_DAYS_CUSTOM_DAYS",
    scheduleStartDate: "",
    scheduleEndDate: "",
    selectedDays: [0, 1, 2, 3, 4, 5, 6],
    gapHandling: "USE_DEFAULT_BUDGET",
    slots: [createDefaultSlot(false)],
  };
}

/** Reset slot/day fields when switching between All Days and Dayparting. */
export function buildAutomationModeResetPatch(prevForm, automationMode) {
  const multiCampaign = (prevForm?.selectedCampaignIds ?? []).length > 1;
  const slot = createDefaultSlot(multiCampaign);

  (prevForm?.selectedCampaignIds ?? []).forEach((mongoId) => {
    slot.campaignBudgets[mongoId] = createDefaultSlotCampaignBudget(multiCampaign);
  });

  return {
    automationMode,
    selectedDays: [0, 1, 2, 3, 4, 5, 6],
    slots: [slot],
  };
}

function buildScheduleConfigForForm(form) {
  const isAllDays = form.automationMode === "ALL_DAYS_CUSTOM_DAYS";
  const freqValues = isAllDays
    ? frequencyValuesFromDayIndices(form.selectedDays)
    : null;

  return {
    type: null,
    day_frequency: isAllDays && freqValues ? "CUSTOM_DAYS" : null,
    frequency_values: isAllDays ? freqValues : null,
    start_hour: null,
    end_hour: null,
    start_date: form.scheduleStartDate || null,
    end_date: form.scheduleEndDate || null,
    timezone: null,
  };
}

function buildDurationConfigForSlot(form, slot) {
  const isDayparting = form.automationMode === "DAYPARTING";

  if (isDayparting) {
    return {
      id: slot.durationId || `dur-${generateId()}`,
      status: slot.status || "active",
      start_date: form.scheduleStartDate || null,
      end_date: form.scheduleEndDate || null,
      start_time: slot.startTime || "09:00",
      end_time: slot.endTime || "18:00",
    };
  }

  return {
    id: slot.durationId || `dur-${generateId()}`,
    status: slot.status || "active",
    start_date: slot.startDate || form.scheduleStartDate || null,
    end_date: slot.endDate || form.scheduleEndDate || null,
    start_time: null,
    end_time: null,
  };
}

function buildEntityMetricFilter(slot, platformIds, userPlatform) {
  const shouldBuildCube =
    slot.perfFilter?.enabled ||
    filterModuleHasContent(slot.perfFilter?.filterModuleFilters) ||
    Boolean(slot.perfFilter?.cubeFilters?.trim());

  const cube = shouldBuildCube
    ? slot.perfFilter.cubeFilters?.trim() ||
      buildCubeFiltersFromFilterModule(slot.perfFilter?.filterModuleFilters, {
        timePeriod: slot.perfFilter?.timePeriod,
        platformCampaignIds: platformIds,
        userPlatform,
      })
    : "";

  return {
    type: "CUBE_BASED_NESTED_AND_OR",
    time_period: slot.perfFilter.timePeriod || "LAST_7_DAY",
    cube_filters: cube || "",
    enable_interval_compounding: Boolean(slot.enableCompounding),
  };
}

function buildEntityConfigsForTask(form, slot) {
  const multi = form.selectedCampaignIds.length > 1;
  return form.selectedCampaignIds.map((mongoId) => {
    const meta = form.campaignMeta[mongoId] ?? {};
    const table = form.campaignTableBudgets[mongoId] ?? {};
    const slotRow = slot.campaignBudgets[mongoId] ?? {};
    const changeType = slotRow.change_type ?? (multi ? "PERCENT_INCREASE" : "PERCENT_INCREASE");
    const changeVal = parseNumOrEmpty(slotRow.change_value);

    const baseVal = parseNumOrEmpty(table.base_value ?? meta.defaultBudget) || 0;
    const minVal = parseNumOrEmpty(table.min_value);
    const maxVal = parseNumOrEmpty(table.max_value);

    return {
      entity_type: "campaign",
      id: mongoId,
      value_constraints: {
        metric: "BUDGET",
        base_value: typeof baseVal === "number" ? baseVal : Number(baseVal) || 0,
        ...(minVal !== "" && { min_value: Number(minVal) }),
        ...(maxVal !== "" && { max_value: Number(maxVal) }),
      },
      change_config: {
        change_type: changeType,
        change_value:
          changeVal === "" ? 0 : typeof changeVal === "number" ? changeVal : Number(changeVal),
      },
      child_entities: null,
    };
  });
}

export function formToApiRule(form, { clientId, email, ruleId, createdAt, userPlatform = "amazon" }) {
  const now = new Date().toISOString();
  const name = form.ruleName.trim();
  const platformIds = form.selectedCampaignIds
    .map((mid) => form.campaignMeta[mid]?.platformId)
    .filter(Boolean);

  const schedule_config = buildScheduleConfigForForm(form);

  const rule_tasks = form.slots.map((slot, index) => {
    const duration = buildDurationConfigForSlot(form, slot);

    const emf = buildEntityMetricFilter(slot, platformIds, userPlatform);

    return {
      rule_task_name: `Duration ${index + 1}`,
      entity_metric_filter: emf,
      entity_configs: buildEntityConfigsForTask(form, slot),
      rule_action_config: {
        entity_action_config: {
          action_type: "UPDATE_BUDGET",
          apply_decision_flag: null,
          percentage_change: null,
          threshold: null,
          entity_lock_duration_days: null,
          entity_type_wise_percentage_change_and_threshold: null,
          duration_config: duration,
        },
        max_percent_exclude: 50,
      },
    };
  });

  return {
    ...(ruleId ? { id: ruleId } : {}),
    status: form.status || "Active",
    client_id: clientId,
    entity_filter: null,
    rule_tasks,
    schedule_config,
    name,
    type: "AMAZON_CAMPAIGN_ABO",
    modified_by: email || "local@user",
    apply_decision: null,
    source: "amazon",
    created_at: createdAt || now,
    updated_at: now,
    rule_objective: "Campaign_ABO",
    rule_config: {
      automation_mode: form.automationMode,
      gap_handling: form.gapHandling,
      guardrails: { ...DEFAULT_GUARD_RAILS },
    },
  };
}

function stripClientGeneratedIdsForCreate(ruleTasks = []) {
  return ruleTasks.map((task) => {
    const duration =
      task?.rule_action_config?.entity_action_config?.duration_config;
    if (!duration?.id) return task;

    const { id, ...durationWithoutId } = duration;
    return {
      ...task,
      rule_action_config: {
        ...task.rule_action_config,
        entity_action_config: {
          ...task.rule_action_config.entity_action_config,
          duration_config: durationWithoutId,
        },
      },
    };
  });
}

/** Request body for POST/PATCH automation-rule-set (matches backend contract). */
export function formToApiRequestBody(form, { email, ruleId, createdAt, clientId, userPlatform }) {
  const rule = formToApiRule(form, { clientId, email, ruleId, createdAt, userPlatform });
  const isCreate = !ruleId;

  const body = {
    type: rule.type,
    name: rule.name,
    status: rule.status,
    rule_objective: rule.rule_objective,
    source: rule.source,
    modified_by: rule.modified_by,
    client_id: rule.client_id,
    apply_decision: rule.apply_decision,
    entity_filter: rule.entity_filter,
    rule_config: rule.rule_config,
    schedule_config: rule.schedule_config,
    rule_tasks: isCreate
      ? stripClientGeneratedIdsForCreate(rule.rule_tasks)
      : rule.rule_tasks,
  };

  if (!isCreate && rule.id) {
    body.id = rule.id;
  }

  return body;
}

export function apiRuleToForm(rule) {
  if (!rule) return createInitialFormState();

  const form = createInitialFormState();
  form.ruleName = rule.name ?? "";
  form.status = rule.status ?? "Active";
  form.automationMode =
    rule.rule_config?.automation_mode ??
    (rule.rule_tasks?.[0]?.rule_action_config?.entity_action_config?.duration_config?.start_time
      ? "DAYPARTING"
      : "ALL_DAYS_CUSTOM_DAYS");
  form.gapHandling = rule.rule_config?.gap_handling ?? "USE_DEFAULT_BUDGET";
  form.scheduleStartDate = formatIsoDateForInput(rule.schedule_config?.start_date);
  form.scheduleEndDate = formatIsoDateForInput(rule.schedule_config?.end_date);
  form.selectedDays = selectedDayIndicesFromFrequency(rule.schedule_config?.frequency_values);
  const campaignIds = [];
  const campaignMeta = {};
  const campaignTableBudgets = {};
  const slots = [];

  (rule.rule_tasks ?? []).forEach((task) => {
    const dc = task.rule_action_config?.entity_action_config?.duration_config ?? {};
    const emf = task.entity_metric_filter;
    const slot = createDefaultSlot(false);
    slot.durationId = dc.id ?? slot.durationId;
    slot.status = dc.status ?? "active";
    slot.startDate = formatIsoDateForInput(dc.start_date);
    slot.endDate = formatIsoDateForInput(dc.end_date);
    slot.startTime = String(dc.start_time ?? "09:00").slice(0, 5);
    slot.endTime = String(dc.end_time ?? "18:00").slice(0, 5);
    if (emf) {
      slot.perfFilter.enabled = Boolean(emf.cube_filters?.toString().trim());
      slot.perfFilter.timePeriod = emf.time_period ?? "LAST_7_DAY";
      slot.perfFilter.customDate = timePeriodToCustomDate(emf.time_period);
      slot.perfFilter.cubeFilters =
        typeof emf.cube_filters === "string"
          ? emf.cube_filters
          : JSON.stringify(emf.cube_filters ?? "");
      slot.perfFilter.filterModuleFilters = hydrateFilterModuleFromCubeFilters(emf.cube_filters);
      slot.enableCompounding = Boolean(emf.enable_interval_compounding);
    }

    const slotCampaignBudgets = {};
    (task.entity_configs ?? []).forEach((ec) => {
      if (ec.entity_type !== "campaign") return;
      const mid = String(ec.id);
      if (!campaignIds.includes(mid)) campaignIds.push(mid);
      const vc = ec.value_constraints ?? {};
      const cc = ec.change_config ?? {};
      campaignTableBudgets[mid] = {
        status: "enabled",
        base_value: vc.base_value ?? "",
        min_value:
          vc.min_value !== "" && vc.min_value != null ? vc.min_value : getMinCampaignBudget(),
        max_value: vc.max_value ?? "",
      };
      slotCampaignBudgets[mid] = {
        change_type: cc.change_type ?? "PERCENT_INCREASE",
        hasCustomType: true,
        change_value: cc.change_value ?? "",
        hasCustomValue: true,
      };
      campaignMeta[mid] = {
        ...(campaignMeta[mid] || {}),
        defaultBudget: vc.base_value,
      };
    });
    slot.campaignBudgets = slotCampaignBudgets;
    slots.push(slot);
  });

  form.selectedCampaignIds = campaignIds;
  form.campaignTableBudgets = campaignTableBudgets;
  form.campaignMeta = campaignMeta;
  form.slots = slots.length ? slots : [createDefaultSlot(campaignIds.length > 1)];

  return form;
}
